import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiGetGrupos } from "../../api/gruposApi";
import { apiGetSubGrupos } from "../../api/subGruposApi";
import {
  apiGetAvailableExcludedActivities,
  apiDeleteExcludedActivityBySubgroup,
  apiGetExcludedActivitiesBySubgroup,
  apiSaveExcludedActivitiesBySubgroup,
} from "../../api/masters/excludedActivitiesApi";

export const useExcludedActivities = () => {
  const [groups, setGroups] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [activities, setActivities] = useState([]);
  const [relations, setRelations] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedSubgroup, setSelectedSubgroup] = useState("");
  const [selectedActivityOptions, setSelectedActivityOptions] = useState([]);
  const [loadingRelations, setLoadingRelations] = useState(false);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hasLoadedRelations, setHasLoadedRelations] = useState(false);

  const loadGroups = useCallback(async () => {
    try {
      const res = await apiGetGrupos("");
      const activeGroups = (res.data || []).filter((group) => String(group.estado) === "1");
      setGroups(activeGroups);
    } catch {
      toast.error("Error cargando grupos");
    }
  }, []);

  const loadSubgroups = useCallback(async (groupId) => {
    if (!groupId) {
      setSubgroups([]);
      return;
    }

    try {
      const res = await apiGetSubGrupos("", groupId);
      const activeSubgroups = (res.data || []).filter((subgroup) => String(subgroup.estado) === "1");
      setSubgroups(activeSubgroups);
    } catch {
      toast.error("Error cargando subgrupos");
    }
  }, []);

  const loadActivities = useCallback(async () => {
    try {
      setLoadingActivities(true);
      const res = await apiGetAvailableExcludedActivities();
      setActivities(res.data || []);
    } catch {
      toast.error("Error cargando actividades");
    } finally {
      setLoadingActivities(false);
    }
  }, []);

  const loadRelations = useCallback(async (groupId = "", subgroupId = "") => {
    if (!subgroupId) {
      setRelations([]);
      setHasLoadedRelations(false);
      return;
    }

    try {
      setLoadingRelations(true);
      const res = await apiGetExcludedActivitiesBySubgroup(groupId, subgroupId);
      setRelations(res.data || []);
      setHasLoadedRelations(true);
    } catch {
      toast.error("Error cargando relaciones configuradas");
    } finally {
      setLoadingRelations(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    if (!selectedSubgroup) {
      setRelations([]);
      setHasLoadedRelations(false);
      return;
    }

    loadRelations(selectedGroup, selectedSubgroup);
  }, [loadRelations, selectedGroup, selectedSubgroup]);

  useEffect(() => {
    loadSubgroups(selectedGroup);
  }, [loadSubgroups, selectedGroup]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  const handleGroupChange = (groupId) => {
    setSelectedGroup(groupId);
    setSelectedSubgroup("");
    setSelectedActivityOptions([]);
    setRelations([]);
    setHasLoadedRelations(false);
  };

  const handleSubgroupChange = (subgroupId) => {
    setSelectedSubgroup(subgroupId);
    setSelectedActivityOptions([]);
    if (!subgroupId) {
      setRelations([]);
      setHasLoadedRelations(false);
    }
  };

  const saveRelations = async () => {
    if (!selectedGroup) {
      toast.warn("Debe seleccionar un grupo");
      return;
    }

    if (!selectedSubgroup) {
      toast.warn("Debe seleccionar un subgrupo");
      return;
    }

    const selectedCodes = (selectedActivityOptions || [])
      .map((opt) => Number(opt.value))
      .filter((v) => Number.isFinite(v) && v > 0);

    if (!selectedCodes.length) {
      toast.warn("Debe seleccionar al menos una actividad");
      return;
    }

    try {
      setSaving(true);

      const res = await apiSaveExcludedActivitiesBySubgroup({
        IdSubgrupo: Number(selectedSubgroup),
        Consecutivos: selectedCodes,
      });

      if (!res.success) {
        toast.error(res.message || "Error guardando relaciones");
        return;
      }

      const inserted = res.data?.insertados ?? 0;
      const skipped = res.data?.omitidos ?? 0;
      const detail = [];

      if (inserted > 0) detail.push(`${inserted} nuevas`);
      if (skipped > 0) detail.push(`${skipped} ya existentes`);

      toast.success(detail.length ? `Relaciones guardadas: ${detail.join(", ")}` : (res.message || "Relaciones guardadas"));
      setSelectedActivityOptions([]);
      loadRelations(selectedGroup, selectedSubgroup);
    } catch {
      toast.error("Error guardando relaciones");
    } finally {
      setSaving(false);
    }
  };

  const deleteRelation = async (relation) => {
    try {
      setDeleting(true);
      const res = await apiDeleteExcludedActivityBySubgroup({
        IdSubgrupo: Number(relation.IdSubgrupo),
        Consecutivo: Number(relation.Consecutivo),
      });

      if (!res.success) {
        toast.error(res.message || "Error eliminando relacion");
        return;
      }

      toast.success(res.message || "Relacion eliminada correctamente");
      loadRelations(selectedGroup, selectedSubgroup);
    } catch {
      toast.error("Error eliminando relacion");
    } finally {
      setDeleting(false);
    }
  };

  return {
    groups,
    subgroups,
    activities,
    relations,
    selectedGroup,
    selectedSubgroup,
    selectedActivityOptions,
    loadingRelations,
    loadingActivities,
    saving,
    deleting,
    hasLoadedRelations,
    setSelectedActivityOptions,
    handleGroupChange,
    handleSubgroupChange,
    saveRelations,
    deleteRelation,
  };
};
